/**
 * Global playback bus — single-song-lock across the entire app.
 *
 * Any component that owns a local <audio> element calls `claim(songId)` when
 * it starts playing, and listens for `claim` events to auto-pause whenever
 * a *different* song claims playback. This lets us keep each component's
 * existing local audio logic (no rewrite of 20+ players) while still
 * guaranteeing only one song plays at a time platform-wide.
 *
 * Usage in a player component:
 *
 *   import { playbackBus, useGlobalPlaybackLock } from '../lib/playbackBus';
 *
 *   // when starting playback:
 *   playbackBus.claim(song.id);
 *   audioRef.current.play();
 *
 *   // when stopping/ending:
 *   playbackBus.release(song.id);
 *
 *   // hook that auto-pauses on competing claims:
 *   useGlobalPlaybackLock(currentSongId, () => {
 *     audioRef.current?.pause();
 *     setIsPlaying(false);
 *   });
 */
import { useEffect } from 'react';

class PlaybackBus extends EventTarget {
  constructor() {
    super();
    this.currentId = null;
  }
  /**
   * Stake a claim for a given song id. If a different song was playing,
   * subscribers will receive a `claim` event and pause themselves.
   */
  claim(songId) {
    if (!songId) return;
    const prevId = this.currentId;
    this.currentId = songId;
    if (prevId !== songId) {
      this.dispatchEvent(new CustomEvent('claim', { detail: { songId, prevId } }));
    }
  }
  release(songId) {
    if (!songId || this.currentId === songId) {
      this.currentId = null;
    }
  }
  isPlaying(songId) {
    return !!songId && this.currentId === songId;
  }
}

export const playbackBus = new PlaybackBus();

/**
 * Hook: subscribe a component to the playback bus. When another song claims
 * playback (different songId from the one this component currently owns),
 * `onPause` fires so the component can pause its local audio.
 */
export function useGlobalPlaybackLock(currentSongId, onPause) {
  useEffect(() => {
    if (!currentSongId) return undefined;
    const handler = (e) => {
      const claimedId = e.detail?.songId;
      if (claimedId && claimedId !== currentSongId) {
        try { onPause && onPause(); } catch {}
      }
    };
    playbackBus.addEventListener('claim', handler);
    return () => playbackBus.removeEventListener('claim', handler);
  }, [currentSongId, onPause]);
}
